import { useEffect, useState } from "react";
import { useAppSelector } from "../../hooks/reduxHooks";
import {
  selectVisibleMessages,
  selectTurnTodoPlan,
  selectChatHistoryLoading,
  selectChatHistoryError,
} from "../../ChatSlice_Store";
import { selectAllFacts } from "../../ResultsSlice_Store";

/** Small delay so the overlay is in the DOM before the fade transition starts. */
const FADE_IN_DELAY_MS = 50;
/** Matches the CSS transition duration on the overlay. */
const FADE_OUT_MS = 300;

export interface ChatData {
  chatMessages: any[];
  isBotTyping: boolean;
  currentWorkflowStatus: any;
  chatHistoryLoading: boolean;
  chatHistoryError: string | null;
  currentTurnTodoPlan: { steps?: any[] } | null;
  allFacts: any[];
  allDocuments: any[];
  streamingMessages: unknown;
  userMessageCount: number;
  showConversationStarters: boolean;
  pillsDisabled: boolean;
}

/** Everything the chat surface reads out of Redux. */
export function useChatData(): ChatData {
  const messages = useAppSelector(selectVisibleMessages);
  const isBotTyping = useAppSelector((state) => state.chatSlice.isBotTyping);
  const currentWorkflowStatus = useAppSelector((state) => state.chatSlice.currentWorkflowStatus);
  const chatHistoryLoading = useAppSelector(selectChatHistoryLoading);
  const chatHistoryError = useAppSelector(selectChatHistoryError);

  // Derive the plan from the workflow status's turnId (not selectCurrentTurnId) to avoid
  // showing a stale plan from a rehydrated previous turn when a new workflow starts.
  const currentTurnId = currentWorkflowStatus?.turnId ?? null;
  const currentTurnTodoPlan = useAppSelector((state) =>
    currentTurnId ? selectTurnTodoPlan(state, currentTurnId) : null
  );

  // Phase 3: Read facts and documents from central ResultsSlice
  const allFacts = useAppSelector(selectAllFacts);
  const allDocuments = useAppSelector((state) => state.resultsSlice?.documentRegistry || []);

  // Phase 8: Streaming messages drive the auto-scroll dependency
  const streamingMessages = useAppSelector((state) => (state as any).streaming?.messages || {});

  const chatMessages = Array.isArray(messages) ? messages : [];
  const userMessageCount = chatMessages.filter((msg) => msg.sender === "user").length;

  return {
    chatMessages,
    isBotTyping,
    currentWorkflowStatus,
    chatHistoryLoading,
    chatHistoryError,
    currentTurnTodoPlan,
    allFacts,
    allDocuments,
    streamingMessages,
    userMessageCount,
    // Show pills only while no user messages exist and history is not loading
    showConversationStarters:
      userMessageCount === 0 && !chatHistoryLoading && chatMessages.length <= 1,
    // Pills are disabled after one bot message beyond the welcome
    pillsDisabled: chatMessages.length > 1,
  };
}

export interface HistoryOverlay {
  showLoadingOverlay: boolean;
  loadingOverlayVisible: boolean;
}

/** Fades the "loading conversation history" modal in and out. */
export function useHistoryOverlay(chatHistoryLoading: boolean): HistoryOverlay {
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(chatHistoryLoading);
  const [loadingOverlayVisible, setLoadingOverlayVisible] = useState(false);

  useEffect(() => {
    if (chatHistoryLoading) {
      // Fade in: show overlay immediately, then fade in after brief delay
      setShowLoadingOverlay(true);
      const fadeInTimer = setTimeout(() => {
        setLoadingOverlayVisible(true);
      }, FADE_IN_DELAY_MS);
      return () => clearTimeout(fadeInTimer);
    } else if (showLoadingOverlay) {
      // Fade out: hide visibility first, then remove from DOM
      setLoadingOverlayVisible(false);
      const timer = setTimeout(() => {
        setShowLoadingOverlay(false);
      }, FADE_OUT_MS);
      return () => clearTimeout(timer);
    }
  }, [chatHistoryLoading, showLoadingOverlay]);

  return { showLoadingOverlay, loadingOverlayVisible };
}

/** Animation delays and the streaming text effect, injected once per mount. */
const SPARKLE_STYLES = `
    .animation-delay-200 { animation-delay: 0.2s; }
    .animation-delay-400 { animation-delay: 0.4s; }
    @keyframes fadeInType {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }

    /* Streaming text fade-in animation */
    @keyframes streamingFadeIn {
      0% {
        opacity: 0;
        filter: blur(1px);
      }
      100% {
        opacity: 1;
        filter: blur(0);
      }
    }

    .streaming-text {
      animation: streamingFadeIn 0.2s ease-in;
    }
  `;

/** Adds the chat's keyframe styles to the document head while mounted. */
export function useChatAnimationStyles(): void {
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = SPARKLE_STYLES;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
}
